import { useState } from "react";

import { ALERT, ErrorRow, usd, useAction, wholeNumber } from "./parts";
import styles from "./Settings.module.css";
import { useReturnNotice } from "./useReturnNotice";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Switch } from "../../components/Switch";
import {
  type ApiAutoTopUp,
  type ApiBillingAccount,
  type ApiBillingSubscription,
  type ApiLedgerEntry,
  type BlockedReason,
  type LedgerKind,
  openBillingPortal,
  startCheckout,
  startTopUp,
  type StripeRedirect,
  type SubscriptionStatus,
  updateAutoTopUp,
  useBilling,
  useBillingLedger,
  type UseBillingLedger,
} from "../../data/api/billing";
import { useCurrentWorkspace } from "../../data/api/currentWorkspace";
import { cx } from "../../lib/cx";

// Settings → Billing (specifications/billing.md): the plan, the compute credit
// balance, auto top-up and recent ledger activity. Payment itself happens on
// Stripe's pages; everything here either reads the account or sends the
// browser there.

const STATUS_WORDS: Readonly<Record<SubscriptionStatus, string>> = {
  active: "Active",
  canceled: "Cancelled",
  incomplete: "Incomplete — the first payment hasn't gone through",
  incomplete_expired: "Expired — the first payment never went through",
  past_due: "Past due — Stripe is retrying the card",
  paused: "Paused",
  trialing: "Trial",
  unpaid: "Unpaid — Stripe has stopped retrying the card",
};

const BLOCKED_WORDS: Readonly<Record<BlockedReason, string>> = {
  no_subscription: "Agents can't start until the workspace subscribes.",
  out_of_credits: "Cloud runners are paused until credits are added.",
};

const LEDGER_WORDS: Readonly<Record<LedgerKind, string>> = {
  auto_top_up: "Auto top-up",
  top_up: "Top-up",
  usage: "Compute",
};

// What Stripe Checkout returns to Settings with (api.md §Billing).
const RETURN_NOTICES: Readonly<Record<string, string>> = {
  cancelled: "Checkout cancelled — nothing was charged.",
  subscribed: "Subscribed — the plan updates once Stripe confirms it.",
  "topped-up": "Payment received — credits appear once Stripe confirms it.",
};

const DEFAULT_TOP_UP_USD = 50;

const signedUsd = (amount: number): string =>
  `${amount < 0 ? "−" : "+"}${usd(amount)}`;

const longDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

// A usage row's day is a UTC day; read in local time it lands a day early
// anywhere west of Greenwich.
const shortDate = (iso: string, timeZone?: string): string =>
  new Date(iso).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    timeZone,
  });

const plural = (n: number, unit: string): string =>
  `${n.toLocaleString("en-US", { maximumFractionDigits: 2 })} ${unit}${n === 1 ? "" : "s"}`;

const planStatus = (subscription: ApiBillingSubscription | null): string => {
  if (!subscription) {
    return "No subscription";
  }
  const { cancel_at_period_end, current_period_end, status } = subscription;
  if (cancel_at_period_end && (status === "active" || status === "trialing")) {
    return current_period_end
      ? `Cancels on ${longDate(current_period_end)}`
      : "Cancels at the end of the period";
  }
  return STATUS_WORDS[status];
};

const ledgerLine = (entry: ApiLedgerEntry): string =>
  [
    entry.usage_date
      ? shortDate(entry.usage_date, "UTC")
      : shortDate(entry.created_at),
    LEDGER_WORDS[entry.kind],
    entry.usage_hours === null
      ? null
      : plural(entry.usage_hours, "runner-hour"),
  ]
    .filter((part) => part !== null)
    .join(" · ");

const toStripe = (redirect: Promise<StripeRedirect>): Promise<void> =>
  redirect.then(({ url }) => window.location.assign(url));

const STRIPE_FAILED = "Couldn't reach Stripe. Try again.";

export const Billing = () => {
  const { isAdmin, workspaceId } = useCurrentWorkspace();
  const { billing, refetch } = useBilling(workspaceId);
  const ledger = useBillingLedger(
    workspaceId,
    isAdmin && billing?.enabled === true,
  );
  const notice = useReturnNotice("billing", RETURN_NOTICES);

  if (!billing?.enabled || !workspaceId) {
    return null;
  }
  return (
    <>
      <PlanSection
        account={billing}
        isAdmin={isAdmin}
        notice={notice}
        workspaceId={workspaceId}
      />
      <CreditsSection
        account={billing}
        isAdmin={isAdmin}
        workspaceId={workspaceId}
      />
      <AutoTopUpSection
        account={billing}
        isAdmin={isAdmin}
        onSaved={refetch}
        workspaceId={workspaceId}
      />
      {isAdmin ? <ActivitySection ledger={ledger} /> : null}
    </>
  );
};

type SectionProps = {
  readonly account: ApiBillingAccount;
  readonly isAdmin: boolean;
  readonly workspaceId: string;
};

const PlanSection = ({
  account,
  isAdmin,
  notice,
  workspaceId,
}: SectionProps & { readonly notice: string | null }) => {
  const { busy, error, run } = useAction();
  const { seats, subscription } = account;
  // The server's answer to whether the subscription lets agents run
  // (billing.md §Seats), rather than a second copy of its status list.
  const admits = account.blocked_reason !== "no_subscription";
  const periodEnd = subscription?.current_period_end ?? null;
  const renews =
    admits && periodEnd && !subscription?.cancel_at_period_end
      ? `renews ${longDate(periodEnd)}`
      : null;
  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Billing</h3>
      {notice ? (
        <div className={styles.returnNotice} role="status">
          {notice}
        </div>
      ) : null}
      <div className={styles.card}>
        <div className={styles.row}>
          <div className={styles.grow}>
            <div className={styles.label}>{planStatus(subscription)}</div>
            <div className={styles.help}>
              {[plural(seats, "seat"), renews].filter(Boolean).join(" · ")}
            </div>
          </div>
          {isAdmin ? (
            <Button
              disabled={busy}
              onClick={() =>
                run(
                  () =>
                    toStripe(
                      admits
                        ? openBillingPortal(workspaceId)
                        : startCheckout(workspaceId),
                    ),
                  STRIPE_FAILED,
                )
              }
              size="sm"
              variant={admits ? "default" : "accent"}
            >
              {admits ? "Manage billing" : "Subscribe"}
            </Button>
          ) : null}
        </div>
        <ErrorRow message={error} />
      </div>
    </section>
  );
};

const CreditsSection = ({ account, isAdmin, workspaceId }: SectionProps) => {
  const { busy, error, run } = useAction();
  const [amount, setAmount] = useState(String(DEFAULT_TOP_UP_USD));
  const { balance_usd, payment_method } = account.credits;
  const { max_usd, min_usd } = account.top_up_limits;
  const topUp = wholeNumber(amount, min_usd, max_usd);
  const owed = balance_usd < 0;

  const addCredits = (): void => {
    if (topUp !== null) {
      run(() => toStripe(startTopUp(workspaceId, topUp)), STRIPE_FAILED);
    }
  };

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Compute credits</h3>
      <div className={styles.card}>
        {account.blocked_reason ? (
          <div className={styles.row}>
            <span className={styles.help} role="status" style={ALERT}>
              {BLOCKED_WORDS[account.blocked_reason]}
            </span>
          </div>
        ) : null}
        <div className={styles.row}>
          <div className={styles.grow}>
            <div className={styles.label}>Balance</div>
            <div className={styles.help}>
              Spent per runner-hour by cloud runners. Self-hosted runners
              aren&apos;t metered.
            </div>
            {owed ? (
              <div className={styles.help}>
                Cloud runners holding sandboxes keep accruing until those tasks
                are archived, so the next top-up pays this down first.
              </div>
            ) : null}
          </div>
          <span className={cx(styles.balance, owed && styles.owed)}>
            {owed ? `−${usd(balance_usd)} owed` : usd(balance_usd)}
          </span>
        </div>
        {isAdmin ? (
          <>
            <div className={styles.row}>
              <div className={styles.grow}>
                <div className={styles.label}>Add credits</div>
                <div className={styles.help}>
                  {usd(min_usd, 0)}–{usd(max_usd, 0)} in whole dollars, before
                  tax, paid on Stripe. The card is saved for auto top-up.
                </div>
              </div>
              <DollarInput
                label="Top-up amount"
                max={max_usd}
                min={min_usd}
                onChange={setAmount}
                value={amount}
              />
              <Button
                disabled={busy || topUp === null}
                onClick={addCredits}
                size="sm"
                variant="accent"
              >
                Add credits
              </Button>
            </div>
            <div className={styles.row}>
              <div className={styles.grow}>
                <div className={styles.label}>Payment method</div>
                <div className={styles.help}>
                  {payment_method ?? "None saved yet."}
                </div>
              </div>
            </div>
          </>
        ) : null}
        <ErrorRow message={error} />
      </div>
    </section>
  );
};

const DollarInput = ({
  disabled,
  label,
  max,
  min,
  onChange,
  value,
}: {
  readonly disabled?: boolean;
  readonly label: string;
  readonly max?: number;
  readonly min: number;
  readonly onChange: (value: string) => void;
  readonly value: string;
}) => (
  <span className={styles.inlineFields}>
    $
    <Input
      aria-label={label}
      className={styles.numberInput}
      disabled={disabled}
      inputMode="numeric"
      max={max}
      min={min}
      onChange={(e) => onChange(e.target.value)}
      step={1}
      type="number"
      value={value}
    />
  </span>
);

type AutoTopUpForm = {
  readonly amount: string;
  readonly enabled: boolean;
  readonly threshold: string;
};

// Stripe's decline messages end in a full stop; the sentence around it adds one.
const stopReason = (lastError: string | null): string =>
  lastError ? lastError.replace(/\.$/, "") : "the card was declined";

const formOf = (saved: ApiAutoTopUp): AutoTopUpForm => ({
  amount: String(saved.amount_usd),
  enabled: saved.enabled,
  threshold: String(saved.threshold_usd),
});

// Not on the page SaveBar: a sensitive write to its own endpoint, saved on its
// own like the credential sections.
const AutoTopUpSection = ({
  account,
  isAdmin,
  onSaved,
  workspaceId,
}: SectionProps & { readonly onSaved: () => void }) => {
  const { busy, error, run } = useAction();
  const saved = account.credits.auto_top_up;
  const { max_usd, min_usd } = account.top_up_limits;
  // Null tracks the server; an object is an unsaved edit.
  const [draft, setDraft] = useState<AutoTopUpForm | null>(null);
  const form = draft ?? formOf(saved);
  const edit = (patch: Partial<AutoTopUpForm>): void =>
    setDraft({ ...form, ...patch });

  const threshold = wholeNumber(form.threshold, 0, max_usd);
  const amount = wholeNumber(form.amount, min_usd, max_usd);
  const noCard = account.credits.payment_method === null;
  const dirty =
    draft !== null && JSON.stringify(draft) !== JSON.stringify(formOf(saved));
  // Saving unchanged settings is how a stopped auto top-up is retried.
  const canSave =
    isAdmin &&
    !busy &&
    threshold !== null &&
    amount !== null &&
    (dirty || saved.failed_at !== null);

  const save = (): void => {
    if (!canSave) {
      return;
    }
    run(
      () =>
        updateAutoTopUp(workspaceId, {
          amount_usd: amount,
          enabled: form.enabled,
          threshold_usd: threshold,
        }).then(() => {
          setDraft(null);
          onSaved();
        }),
      "Couldn't save auto top-up.",
    );
  };

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Auto top-up</h3>
      <div className={styles.card}>
        {saved.failed_at ? (
          <div className={styles.row}>
            <span className={styles.help} role="alert" style={ALERT}>
              {`Auto top-up stopped: ${stopReason(saved.last_error)}. Save again or update the card to retry.`}
            </span>
          </div>
        ) : null}
        <div className={styles.row}>
          <div className={styles.grow}>
            <div className={styles.label}>Top up automatically</div>
            <div className={styles.help}>
              {noCard
                ? "Add credits once to save a card, then turn this on."
                : `Charges ${account.credits.payment_method}, plus any tax, when the balance runs low.`}
            </div>
          </div>
          <Switch
            aria-label="Auto top-up"
            checked={form.enabled}
            // Turning it off stays possible after the card has gone.
            disabled={!isAdmin || (noCard && !form.enabled)}
            onCheckedChange={(enabled) => edit({ enabled })}
          />
        </div>
        <div className={styles.row}>
          <div className={cx(styles.grow, styles.inlineFields)}>
            When the balance falls below
            <DollarInput
              disabled={!isAdmin}
              label="Auto top-up threshold"
              max={max_usd}
              min={0}
              onChange={(value) => edit({ threshold: value })}
              value={form.threshold}
            />
            add
            <DollarInput
              disabled={!isAdmin}
              label="Auto top-up amount"
              max={max_usd}
              min={min_usd}
              onChange={(value) => edit({ amount: value })}
              value={form.amount}
            />
          </div>
          {isAdmin ? (
            <Button
              disabled={!canSave}
              onClick={save}
              size="sm"
              variant="accent"
            >
              Save
            </Button>
          ) : null}
        </div>
        {isAdmin && (threshold === null || amount === null) ? (
          <ErrorRow
            message={`Use whole dollars: a threshold of $0–${usd(max_usd, 0)}, and an amount of ${usd(min_usd, 0)}–${usd(max_usd, 0)}.`}
          />
        ) : null}
        <ErrorRow message={error} />
      </div>
    </section>
  );
};

const ActivitySection = ({ ledger }: { readonly ledger: UseBillingLedger }) => (
  <section className={styles.section}>
    <h3 className={styles.sectionTitle}>Billing activity</h3>
    <div className={styles.card}>
      {ledger.entries.length === 0 ? (
        <div className={styles.row}>
          <span className={styles.help}>
            {ledger.error
              ? ledger.error.message
              : ledger.loading
                ? "Loading activity…"
                : "No activity yet."}
          </span>
        </div>
      ) : null}
      {ledger.entries.map((entry) => (
        <div className={styles.row} key={entry.public_id}>
          <span className={styles.grow}>{ledgerLine(entry)}</span>
          <span className={styles.amount}>{signedUsd(entry.amount_usd)}</span>
        </div>
      ))}
    </div>
  </section>
);
